import { useState } from "react";
import axios from "axios";
import { Mail, Instagram, Youtube, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { BRAND } from "../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent. We'll reply soon.");
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20" data-testid="contact-page">
      <div className="text-xs uppercase tracking-widest text-[#1D4ED8]">Contact</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[1.1]">Talk to Zentry.</h1>
      <p className="text-lg text-zinc-600 mt-6 max-w-2xl">The fastest way to reach us is through the Clarity Call form. For anything else — use the form below or write to us directly.</p>

      <div className="grid md:grid-cols-12 gap-10 mt-14">
        <div className="md:col-span-5 space-y-6">
          <a href={`mailto:${BRAND.contact.email}`} className="block bg-white border border-zinc-200 rounded-2xl p-6 hover:border-[#1D4ED8]" data-testid="contact-email-card">
            <Mail className="h-6 w-6 text-[#1D4ED8]" />
            <div className="text-xs uppercase tracking-widest text-zinc-500 mt-4">Email</div>
            <div className="font-display text-2xl mt-1">{BRAND.contact.email}</div>
          </a>
          <a href={BRAND.contact.instagramUrl} target="_blank" rel="noreferrer" className="block bg-white border border-zinc-200 rounded-2xl p-6 hover:border-[#1D4ED8]" data-testid="contact-ig-card">
            <Instagram className="h-6 w-6 text-[#1D4ED8]" />
            <div className="text-xs uppercase tracking-widest text-zinc-500 mt-4">Instagram</div>
            <div className="font-display text-2xl mt-1">@{BRAND.contact.instagram}</div>
          </a>
          <a href={BRAND.contact.youtubeUrl} target="_blank" rel="noreferrer" className="block bg-white border border-zinc-200 rounded-2xl p-6 hover:border-[#1D4ED8]" data-testid="contact-yt-card">
            <Youtube className="h-6 w-6 text-[#1D4ED8]" />
            <div className="text-xs uppercase tracking-widest text-zinc-500 mt-4">YouTube</div>
            <div className="font-display text-2xl mt-1">@{BRAND.contact.youtube}</div>
          </a>
        </div>

        <div className="md:col-span-7">
          {sent ? (
            <div className="bg-white border border-zinc-200 rounded-3xl p-10" data-testid="contact-success">
              <div className="font-display text-3xl">Message sent.</div>
              <p className="text-zinc-600 mt-3">We'll respond within 24 hours at {form.email}.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-3xl p-8 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" data-testid="contact-name" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" data-testid="contact-email" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Phone (optional)</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" data-testid="contact-phone" />
                </div>
                <div>
                  <Label>Subject</Label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-2" data-testid="contact-subject" />
                </div>
              </div>
              <div>
                <Label>Message</Label>
                <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2" data-testid="contact-message" />
              </div>
              <button type="submit" disabled={sending} className="btn-primary" data-testid="contact-submit">
                <Send className="h-4 w-4" /> {sending ? "Sending..." : "Send message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
